import {
  CButton,
  CFormLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSpinner,
} from '@coreui/react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useState } from 'react';

dayjs.extend(utc);

interface DateModalProps {
  visible: boolean;
  loading?: boolean;
  currentDate?: string | null;
  onConfirm: (date: string) => void;
  onCancel: () => void;
}

export const DateModal: React.FC<DateModalProps> = ({
  visible,
  loading = false,
  currentDate,
  onConfirm,
  onCancel,
}) => {
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(() =>
    currentDate ? dayjs(currentDate) : null,
  );
  const [showError, setShowError] = useState(false);
  const resetKey = `${visible}:${currentDate ?? ''}`;
  const [previousResetKey, setPreviousResetKey] = useState(resetKey);

  if (resetKey !== previousResetKey) {
    setPreviousResetKey(resetKey);
    setSelectedDate(visible && currentDate ? dayjs(currentDate) : null);
    setShowError(false);
  }

  const handleConfirm = () => {
    if (!selectedDate) {
      setShowError(true);
      return;
    }

    const tomorrow = dayjs().add(1, 'day').startOf('day');
    const maxDate = dayjs().add(6, 'month').startOf('day');

    if (selectedDate.isBefore(tomorrow) || selectedDate.isAfter(maxDate)) {
      setShowError(true);
      return;
    }

    const formattedDate = selectedDate.format('YYYY-MM-DD');

    onConfirm(formattedDate);
    setSelectedDate(null);
    setShowError(false);
  };

  const handleCancel = () => {
    onCancel();
    setSelectedDate(null);
    setShowError(false);
  };

  const minDate = dayjs().add(1, 'day');
  const maxDate = dayjs().add(6, 'month');

  return (
    <CModal visible={visible} onClose={handleCancel}>
      <CModalHeader>
        <CModalTitle>Change Next Dispatch Date</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <div className="mb-3">
          <CFormLabel htmlFor="dispatch-date">Select new dispatch date:</CFormLabel>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              minDate={minDate}
              maxDate={maxDate}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  id: 'dispatch-date',
                  disabled: loading,
                  fullWidth: true,
                  size: 'small',
                },
              }}
            />
          </LocalizationProvider>
          <div className="form-text text-muted mt-1">
            The next dispatch date must be between tomorrow and within 6 months
          </div>
        </div>

        {showError && (
          <div className="text-danger small">
            {!selectedDate
              ? 'Please select a date'
              : 'Selected date must be between tomorrow and within 6 months'}
          </div>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={handleCancel} disabled={loading}>
          Cancel
        </CButton>
        <CButton color="primary" onClick={handleConfirm} disabled={!selectedDate || loading}>
          {loading ? <CSpinner size="sm" className="me-2" /> : null}
          Change Date
        </CButton>
      </CModalFooter>
    </CModal>
  );
};
